import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, TemplateRef } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { BrandingService } from '@services/branding/branding.service';
import { ViewportService } from '@services/geo/viewport.service';
import { MIN_PASSWORD_LENGTH, ValidationService } from '@services/ui/validation.service';
import { ButtonComponent } from '@shared/components/button/button.component';
import { InputComponent } from '@shared/components/input/input.component';
import { SharedModule } from '@shared/modules/shared.module';

@Component({
	selector: 'app-auth-form',
	standalone: true,
	imports: [SharedModule, ButtonComponent, InputComponent],
	templateUrl: './auth-form.component.html',
	styleUrl: './auth-form.component.scss',
})
export class AuthFormComponent implements OnChanges, OnInit {
	@Input() isRegister!: 1 | 2 | 3 | 4 | 5; // 1 = Login, 2 = Reset Password, 3 = Reset Password Confirm, 4 = MFA Verification, 5 = Create Account
	@Output() formSubmit = new EventEmitter<{
		email?: string;
		password?: string;
		newPassword?: string;
		confirmPassword?: string;
		mfaCode?: string;
		trustDevice?: boolean;
	}>();
	@Output() cancelMFA = new EventEmitter<void>();
	@Output() googleLogin = new EventEmitter<void>();
	@Output() appleLogin = new EventEmitter<void>();
	@Output() switchToLogin = new EventEmitter<void>();
	@Output() switchToRegister = new EventEmitter<void>();
	@Output() switchToResetPassword = new EventEmitter<void>();
	@Input() errorMessage: string | null = null;
	@Input() showSuccessState = false;
	@Input() loading = false;
	@Input() isModal = false; // Flag to determine if used in modal or standalone page
	// Rendered under the login form; a template (not ng-content) because the form is stamped twice (desktop + mobile).
	@Input() loginExtra: TemplateRef<unknown> | null = null;

	// MFA State
	mfaCode: string[] = ['', '', '', '', '', ''];
	trustDevice = false;

	// Form
	authForm: FormGroup;
	submitted = false;

	// Branding
	brandName = 'Beltrashow';
	brandLogo: string | null = null;

	constructor(
		private fb: FormBuilder,
		private validationService: ValidationService,
		private viewportService: ViewportService,
		private router: Router,
		private translocoService: TranslocoService,
		private brandingService: BrandingService,
	) {
		this.authForm = this.fb.group({});
	}

	get formFields(): string[] {
		switch (this.isRegister) {
			case 1:
				return ['email', 'password'];
			case 2:
				return ['email'];
			case 3:
				return ['newPassword', 'confirmPassword'];
			case 4:
				return [];
			case 5:
				return ['email', 'password'];
			default:
				return [];
		}
	}

	get isMfaCodeComplete(): boolean {
		return this.mfaCode.every((digit) => digit.length === 1);
	}

	get showSocialButtons(): boolean {
		return false; // TODO: enable when Google/Apple login is ready
	}

	get isMobile(): boolean {
		return this.viewportService.isMobileView();
	}

	get decorativeContent(): { title: string; text: string; cta: string; ctaAction: () => void } {
		switch (this.isRegister) {
			case 5:
				return {
					title: this.translocoService.translate('auth.decorative.signup.title'),
					text: this.translocoService.translate('auth.decorative.signup.text'),
					cta: this.translocoService.translate('auth.decorative.signup.cta'),
					ctaAction: () => this.navigateToLogin(),
				};
			case 2:
			case 3:
				return {
					title: this.translocoService.translate('auth.decorative.reset.title'),
					text: this.translocoService.translate('auth.decorative.reset.text'),
					cta: this.translocoService.translate('auth.decorative.reset.cta'),
					ctaAction: () => this.navigateToLogin(),
				};
			default:
				return {
					title: this.translocoService.translate('auth.decorative.login.title'),
					text: this.translocoService.translate('auth.decorative.login.text'),
					cta: this.translocoService.translate('auth.decorative.login.cta'),
					ctaAction: () => this.navigateToCreateAccount(),
				};
		}
	}

	ngOnInit(): void {
		this.updateFormValidations();
		// Load branding info to apply theme colors
		this.brandingService.load().subscribe({
			next: (branding) => {
				this.brandName = branding.name;
				this.brandLogo = branding.logo || null;
			},
			error: (err) => {
				console.error('❌ Error loading branding in auth-form:', err);
			},
		});
	}

	ngOnChanges(changes: { isRegister?: SimpleChanges }): void {
		if (changes.isRegister) {
			this.updateFormValidations();
		}
	}

	private updateFormValidations(): void {
		switch (this.isRegister) {
			case 1:
				this.authForm = this.fb.group({
					email: ['', [Validators.required, this.validationService.emailValidator()]],
					password: ['', [Validators.required]],
				});
				break;
			case 2:
				this.authForm = this.fb.group({
					email: ['', [Validators.required, this.validationService.emailValidator()]],
				});
				break;
			case 3:
				this.authForm = this.fb.group({
					newPassword: ['', [Validators.required, Validators.minLength(MIN_PASSWORD_LENGTH)]],
					confirmPassword: [
						'',
						[Validators.required, this.validationService.confirmPasswordValidator('newPassword')],
					],
				});
				break;
			case 5:
				this.authForm = this.fb.group({
					email: ['', [Validators.required, this.validationService.emailValidator()]],
					// New account: minimum length (login keeps accepting older, shorter passwords)
					password: ['', [Validators.required, Validators.minLength(MIN_PASSWORD_LENGTH)]],
				});
				break;
			default:
				this.authForm = this.fb.group({});
		}
	}

	onFormFocusIn(): void {
		this.submitted = false;
	}

	onSubmit(): void {
		if (this.loading) return;

		if (this.isRegister === 4) {
			this.onMfaSubmit();
			return;
		}

		this.submitted = true;

		if (this.authForm.invalid) {
			return;
		}

		let formData: any = {};

		switch (this.isRegister) {
			case 1:
			case 2:
				formData = {
					email: this.authForm.get('email')?.value,
					password: this.authForm.get('password')?.value,
				};
				break;
			case 3:
				formData = {
					newPassword: this.authForm.get('newPassword')?.value,
					confirmPassword: this.authForm.get('confirmPassword')?.value,
				};
				break;
			case 5:
				formData = {
					email: this.authForm.get('email')?.value,
					password: this.authForm.get('password')?.value,
				};
				break;
		}

		this.formSubmit.emit(formData);
	}

	onMfaSubmit(): void {
		if (!this.isMfaCodeComplete || this.loading) return;
		this.formSubmit.emit({
			mfaCode: this.mfaCode.join(''),
			trustDevice: this.trustDevice,
		});
	}

	onCancelMFA(): void {
		this.mfaCode = ['', '', '', '', '', ''];
		this.trustDevice = false;
		this.cancelMFA.emit();
	}

	onMfaFocus(event: Event): void {
		(event.target as HTMLInputElement).select();
	}

	onMfaInput(event: Event, index: number): void {
		const input = event.target as HTMLInputElement;
		const numericValue = input.value.replace(/\D/g, '');
		input.value = numericValue.length > 1 ? numericValue.charAt(numericValue.length - 1) : numericValue;
		this.mfaCode[index] = input.value;

		if (input.value && index < 5) {
			const nextInput = input.parentElement?.nextElementSibling?.querySelector('input') as HTMLInputElement;
			if (nextInput) {
				nextInput.focus();
				nextInput.select();
			}
		}
	}

	onMfaKeydown(event: KeyboardEvent, index: number): void {
		const input = event.target as HTMLInputElement;

		if (event.key === 'Enter') {
			event.preventDefault();
			if (this.mfaCode.join('').length === 6) this.onSubmit();
			return;
		}

		if (event.key === 'Backspace' && !input.value && index > 0) {
			const prevInput = input.parentElement?.previousElementSibling?.querySelector('input');
			if (prevInput) (prevInput as HTMLInputElement).focus();
		}

		if (event.key === 'v' && (event.ctrlKey || event.metaKey)) {
			event.preventDefault();
			navigator.clipboard.readText().then((text) => {
				const digits = text.replace(/\D/g, '').slice(0, 6).split('');
				digits.forEach((digit, i) => {
					if (i < 6) {
						this.mfaCode[i] = digit;
						const el = document.querySelector(`#mfa-${i}`) as HTMLInputElement;
						if (el) el.value = digit;
					}
				});
				const lastIndex = Math.min(digits.length - 1, 5);
				const lastInput = document.querySelector(`#mfa-${lastIndex}`) as HTMLInputElement;
				if (lastInput) {
					lastInput.focus();
					lastInput.select();
				}
			});
		}
	}

	getErrorMessage(fieldName: string): string | null {
		const control = this.authForm.get(fieldName);
		if (!control || !control.errors) return null;

		if (control.errors['required']) return this.translocoService.translate('auth.errors.required');
		if (control.errors['invalidPassword'])
			return this.validationService.getValidatorErrorMessage('invalidPassword');
		if (control.errors['passwordsDoNotMatch'])
			return this.translocoService.translate('auth.errors.passwordsDoNotMatch');

		const errorKey = Object.keys(control.errors)[0];
		// With the validator's params ({{requiredLength}} for minlength)
		return errorKey ? this.validationService.getValidatorErrorMessage(errorKey, control.errors[errorKey]) : null;
	}

	submitOnEnter(event: KeyboardEvent): void {
		if (event.key === 'Enter') {
			event.preventDefault();
			this.onSubmit();
		}
	}

	submitOnEnterSafe(event: Event): void {
		this.submitOnEnter(event as KeyboardEvent);
	}

	onForgotPasswordClick(): void {
		if (this.isModal) {
			this.switchToResetPassword.emit();
		} else {
			this.router.navigate(['/reset-password']);
		}
	}

	navigateToLogin(): void {
		if (this.isModal) {
			this.switchToLogin.emit();
		} else {
			this.router.navigate(['/login']);
		}
	}

	navigateToCreateAccount(): void {
		if (this.isModal) {
			this.switchToRegister.emit();
		} else {
			this.router.navigate(['/register']);
		}
	}
}
